"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";
import { ScrollArea } from "./scroll-area";
import {
  BackdropDismissProvider,
  useBackdropDismiss,
} from "../lib/backdrop-dismiss";
import { usePopupExitTeardown } from "../lib/popup-exit-teardown";

function Dialog({ actionsRef, ...props }: DialogPrimitive.Root.Props) {
  // See usePopupExitTeardown: without this, a dialog closing at the moment its
  // panel is hidden by React <Activity> strands its full-screen backdrop in
  // the body — invisible, but swallowing every tap until a reload.
  const exitActions = usePopupExitTeardown(actionsRef);
  // Backdrop dismissal is reconstructed from the press instead of riding a
  // synthesized click — see backdrop-dismiss.tsx.
  const dismiss = React.useMemo(
    () => ({ close: () => exitActions.current?.close() }),
    [exitActions],
  );

  return (
    <BackdropDismissProvider value={dismiss}>
      <DialogPrimitive.Root actionsRef={exitActions} {...props} />
    </BackdropDismissProvider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  const dismissRef = useBackdropDismiss();

  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 transition-opacity duration-200 data-starting-style:opacity-0 data-ending-style:opacity-0",
        className,
      )}
      {...props}
      ref={dismissRef}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      {/* Base UI suppresses a nested dialog's visible backdrop by default.
          This dialog is a distinct modal layer, so keep its scrim and its own
          outside-press target when opened from a Sheet or another Dialog. */}
      <DialogOverlay forceRender />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          // grid-cols-[minmax(0,1fr)]: without an explicit minimum, unshrinkable
          // content (nowrap/truncated text) widens the shared column track past
          // the panel and every child — footer included — overflows and clips.
          "max-h-[calc(100%-2rem)] no-scrollbar overflow-x-hidden overflow-y-auto bg-surface-base fixed top-[50%] left-[50%] z-50 grid grid-cols-[minmax(0,1fr)] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg outline-none sm:max-w-lg transition-[opacity,scale] duration-200 data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="flex items-center justify-center size-11 ring-offset-background focus:ring-ring absolute top-2.5 right-2.5 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

/** A bounded dialog with stationary chrome and an explicitly scrolling DialogBody.
 * Keep min-h-0 on intermediate flex/grid containers (for example Tabs).
 * Width, height, padding and grid layouts remain caller-owned.
 */
function ScrollableDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  return (
    <DialogContent
      className={cn(
        "flex min-h-0 flex-col overflow-hidden [&>[data-slot=dialog-header]]:shrink-0 [&>[data-slot=dialog-footer]]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

/** Use scrollArea only when the existing surface needs the shared custom scrollbar.
 * Safe-area padding is intentionally caller-owned: centered dialogs have no edge inset.
 */
const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & { scrollArea?: boolean }
>(function DialogBody({ className, scrollArea = false, ...props }, ref) {
  if (scrollArea) {
    return (
      <ScrollArea
        ref={ref}
        className={cn(
          "min-h-0 min-w-0 flex-1 isolate transform-gpu [&_[data-slot=scroll-area-viewport]]:overscroll-contain",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <div
      ref={ref}
      data-slot="dialog-body"
      className={cn(
        "min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain",
        className,
      )}
      {...props}
    />
  );
});

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  ScrollableDialogContent,
  DialogBody,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
